import { Breadcrumb, Button, Card, Descriptions, Popconfirm, Space, Tag, Typography } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Can } from '@/shared/auth/Can'
import { formatMoneyValue } from '@/shared/lib/money'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import type { OperationRunner } from '@/shared/ui/useOperationRunner'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import {
  ancestorChain,
  orgTypeName,
  setOrgNodeState,
  typeOf,
  type OrgNode,
  type OrgNodeState,
  type OrgNodeType,
} from './orgApi'
import { ORG_STATE_COLOR, canHoldChildren, childTypes, describeType } from './orgSetup'

interface OrgNodeDetailsProps {
  node: OrgNode
  nodes: readonly OrgNode[]
  types: readonly OrgNodeType[]
  currency: string
  runner: OperationRunner
  onEdit: () => void
  onMove: () => void
  onAdd: (kind: string) => void
}

export function OrgNodeDetails({
  node,
  nodes,
  types,
  currency,
  runner,
  onEdit,
  onMove,
  onAdd,
}: OrgNodeDetailsProps) {
  const { t } = useTranslation()
  const actors = useActorDirectory()
  const type = typeOf(types, node.kind)
  const parentChain = ancestorChain(nodes, node.parentId)
  const addable = canHoldChildren(node, types) ? childTypes(types, node.kind) : []

  const changeState = (next: OrgNodeState) => {
    void runner.run(() => setOrgNodeState(node.id, next))
  }

  return (
    <Card
      title={node.displayName}
      extra={
        <Can permission="org.node.write">
          <Space size="small">
            <Button size="small" onClick={onEdit}>
              {t('common.edit')}
            </Button>
            <Button size="small" danger onClick={onMove}>
              {t('org.move.button')}
            </Button>
          </Space>
        </Can>
      }
    >
      {runner.result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert
            partialNote={false}
            result={runner.result}
            closable
            onClose={runner.clear}
          />
        </div>
      ) : null}

      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label={t('org.node.type')}>{type ? orgTypeName(type, t) : node.kind}</Descriptions.Item>
        <Descriptions.Item label={t('org.node.location')}>
          {parentChain.length === 0 ? (
            t('org.node.topLevel')
          ) : (
            <Breadcrumb items={parentChain.map((item) => ({ title: item.displayName }))} />
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('org.node.manager')}>
          {actorLabel(node.managerUserId, actors, t('org.node.noManager'))}
        </Descriptions.Item>
        <Descriptions.Item label={t('org.node.state')}>
          <Tag color={ORG_STATE_COLOR[node.state]}>{t(`orgNodeState.${node.state}`)}</Tag>
        </Descriptions.Item>
        {type ? (
          <Descriptions.Item label={t('org.node.canDo')}>{describeType(type, t)}</Descriptions.Item>
        ) : null}
        {type?.settlementParty ? (
          <>
            <Descriptions.Item label={t('org.node.settlementMode')}>
              {node.settlementMode === null
                ? t('common.noValue')
                : t(`settlementMode.${node.settlementMode}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('org.node.debtLimit')}>
              {node.debtLimit === null
                ? t('agents.debtLimitNotSet')
                : formatMoneyValue({ amount: node.debtLimit, currency })}
            </Descriptions.Item>
          </>
        ) : null}
        <Descriptions.Item label={t('org.node.code')}>
          <Typography.Text type="secondary" style={{ direction: 'ltr' }}>
            {node.code}
          </Typography.Text>
        </Descriptions.Item>
      </Descriptions>

      <Can permission="org.node.write">
        {addable.length > 0 ? (
          <Space size="small" wrap style={{ marginTop: 16 }}>
            {addable.map((child) => (
              <Button key={child.code} icon={<PlusOutlined />} onClick={() => onAdd(child.code)}>
                {orgTypeName(child, t)}
              </Button>
            ))}
          </Space>
        ) : null}

        <Space size="small" wrap style={{ marginTop: 16, display: 'flex' }}>
          {node.state === 'ACTIVE' ? (
            <Button size="small" loading={runner.running} onClick={() => changeState('SUSPENDED')}>
              {t('org.node.suspend')}
            </Button>
          ) : null}
          {node.state === 'SUSPENDED' ? (
            <Button size="small" loading={runner.running} onClick={() => changeState('ACTIVE')}>
              {t('org.node.activate')}
            </Button>
          ) : null}
          {node.state === 'CLOSED' ? null : (
            <Popconfirm
              title={t('org.node.closeTitle', { name: node.displayName })}
              description={t('org.node.closeConfirm')}
              okText={t('org.node.close')}
              okButtonProps={{ danger: true }}
              cancelText={t('common.cancel')}
              onConfirm={() => changeState('CLOSED')}
            >
              <Button size="small" danger loading={runner.running}>
                {t('org.node.close')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      </Can>
    </Card>
  )
}
